import { types } from "../../types/types";

/**
 * Reducer para el contexto de chat
 *
 * @param {object} state - Estado actual
 * @param {object} action - Accion a realizar
 * @returns {object} - Nuevo estado
 */
export const chatReducer = (state, action) => {
  switch (action.type) {
    // Cargar agentes
    case types.loadAgentes:
      return {
        ...state,
        agentes: [...action.payload],
      };

    // Cargar roles
    case types.loadRoles:
      return {
        ...state,
        roles: [...action.payload],
      };

    // Cargar etiquetas
    case types.cargarMarks:
      return {
        ...state,
        etiquetas: [...action.payload],
      };

    // Cierra la sesion
    case types.cerrarSesion:
      return {
        agentStats: [], // Estadisticas de los agentes
        totalBotMessages: 0, // Total de mensajes del bot
        totalAgentMessages: 0, // Total de mensajes de los agentes
        unreadMessages: 0, // Total de mensajes no leidos
        chatOnHold: 0, // Chats en espera
        estDash: {}, // Datos de Gupshup
        answers: [], // Respuestas rapidas
        chatList: [], // Lista de usuarios
        chatsLists: [], // Lista de usuarios
        messages: [], // Historial de mensajes
        activeChat: null, // Chat activo
        colorMode: "light", // Modo de color
      };

    // Estadisticas de agentes
    case types.nuevasEstadisticas:
      return {
        ...state,
        agentStats: [...action.payload],
      };

    // Mensajes del bot
    case types.nuevosMensajesBot:
      return {
        ...state,
        totalBotMessages: action.payload,
      };

    // Mensajes no leidos
    case types.unreadMessages:
      return {
        ...state,
        unreadMessages: action.payload,
      };

    // Chats en espera
    case types.chatOnHold:
      return {
        ...state,
        chatOnHold: action.payload,
      };

    // Estadisticas de Gupshup
    case types.estDash:
      return {
        ...state,
        estDash: action.payload,
      };

    // Estadisticas de agentes
    case types.totalAgentMessages:
      return {
        ...state,
        totalAgentMessages: action.payload,
      };

    // Respuestas rapidas
    case types.answers:
      return {
        ...state,
        answers: [...action.payload],
      };

    // ************** CHAT ********************

    // Cargar lista de chats
    case types.loadChats:
      return {
        ...state,
        chatList: [...action.payload],
      };

    //! Cargar lista de chats por categorias
    case types.loadChatsCat:
      return {
        ...state,
        chatsLists: action.payload,
      };
    case types.moveChatToCategory: {
      const { chatId, from, to } = action.payload;

      const fromList = state.chatsLists[from] || [];
      const toList = state.chatsLists[to] || [];

      // Encuentra el chat
      const movedChat = fromList.find((chat) => chat.id === chatId);

      if (!movedChat) return state; // Si no lo encuentra, no hace nada

      return {
        ...state,
        chatsLists: {
          ...state.chatsLists,
          [from]: fromList.filter((chat) => chat.id !== chatId), // Elimina del original
          [to]: [...toList, movedChat], // Agrega al nuevo
        },
      };
    }

    // Cargar más chats
    case types.loadMoreChats:
      return {
        ...state,
        chatList: [...state.chatList, ...action.payload],
      };

    // Limpiar lista de chats
    case types.clearChatList:
      return {
        ...state,
        chatList: [],
      };

    // Seleccionar chat
    case types.selectChat:
      return {
        ...state,
        activeChat: action.payload,
      };
    case types.selectChatTab:
      return {
        ...state,
        activeChatTab: action.payload,
      };

    // Cerrar chat
    case types.closeChat:
      return {
        ...state,
        activeChat: null,
        messages: [],
      };

    // Cargar mensajes
    case types.loadMessages:
      return {
        ...state,
        messages: [...action.payload],
      };

    // Cargar más mensajes
    case types.loadMoreMessages:
      return {
        ...state,
        messages: [...state.messages, ...action.payload],
      };

    // Nuevo mensaje
    case types.newMessage:
      return {
        ...state,
        messages: [
          action.payload,
          ...state.messages.filter((msg) => msg.id !== action.payload.idsms),
        ],
      };

    // Nuevo mensaje en el chat activo en la lista de chats
    case types.newMessageOnChat:
      const updatedChat = action.payload;
      const updatedChatsLists = {};

      const targetCategory =
        updatedChat.catname?.toLowerCase() || "bandeja_de_entrada";

      for (const category in state.chatsLists) {
        const chats = state.chatsLists[category];

        // Si es la categoría donde debe estar
        if (category === targetCategory) {
          updatedChatsLists[category] = [
            updatedChat,
            ...chats.filter((chat) => chat.id !== updatedChat.id),
          ];
        } else {
          // En otras categorías, asegurarse de que NO esté
          updatedChatsLists[category] = chats.filter(
            (chat) => chat.id !== updatedChat.id
          );
        }
      }
      return {
        ...state,
        chatList: [
          action.payload,
          ...state.chatList.filter((chat) => chat.id !== action.payload.id),
        ],

        chatsLists: updatedChatsLists,
      };

    case types.removeChatUser:
      const filteredChatsLists = Object.fromEntries(
        Object.entries(state.chatsLists).map(([categoria, chats]) => [
          categoria,
          chats.filter((chat) => chat.id !== action.payload.id),
        ])
      );

      return {
        ...state,
        chatsLists: filteredChatsLists,
      };

    // Nuevo mensaje en un chat en la lista de chats
    case types.newChat:
      return {
        ...state,
        chatList: [
          action.payload,
          ...state.chatList.filter((chat) => chat.id !== action.payload.id),
        ],
        chatLists: [
          action.payload,
          ...state.chatsLists.filter((chat) => chat.id !== action.payload.id),
        ],
      };

    // Actualizar mensaje
    case types.updateMessage:
      return {
        ...state,
        messages: state.messages.map((message) =>
          message.idsms === action.payload.idsms ? action.payload : message
        ),
      };

    // Cambiar etiqueta
    case types.tagChange:
      const updatedChatList = state.chatList.map((chat) =>
        chat.id === action.payload.id ? action.payload : chat
      );
      const updatedChatsListsS = Object.fromEntries(
        Object.entries(state.chatsLists).map(([categoria, chats]) => [
          categoria,
          chats.map((chat) =>
            chat.id === action.payload.id ? action.payload : chat
          ),
        ])
      );
      return {
        ...state,
        chatList: updatedChatList,
        chatsLists: updatedChatsListsS,
      };

    // Actualizar todos los chats
    case types.refreshChatList:
      return {
        ...state,
        chatList: state.chatList.map((chat) =>
          chat.id === action.payload.id ? action.payload : chat
        ),
        chatsLists: Object.fromEntries(
          Object.entries(state.chatsLists).map(([categoria, chats]) => [
            categoria,
            chats.map((chat) =>
              chat.id === action.payload.id ? action.payload : chat
            ),
          ])
        ),
      };

    // Cambiar modo de color
    case types.changeColorMode:
      return {
        ...state,
        colorMode: action.payload,
      };

    // Limpiar mensajes
    case types.clearMessages:
      return {
        ...state,
        messages: [],
      };

    // ************** CONFIGURACION ********************

    // Cargar configuracion del agente
    case types.loadConfig:
      return {
        ...state,
        incomingSound: action.payload.incomingSound,
        outgoingSound: action.payload.outgoingSound,
      };

    default:
      return state;
  }
};
